/**
 * ドメイン制限ファクトチェック用サービス
 * Tavily API + Geminiを使用したリアルタイムファクト検証
 */

import { VERDICT_LABELS, verdictKeyFromLabel } from '../theme/designTokens';
import type {
  ApiResponse,
  DomainVerificationResult,
  QueryGenerationResponse,
  TavilyTimeRange,
} from '../types';
import { apiRequest } from '../utils/api';

/**
 * クエリ生成リクエスト
 */
export interface GenerateQueriesRequest {
  user_input: string;
}

/**
 * ドメイン検証リクエスト
 */
export interface VerifyWithDomainsRequest {
  claim: string;
  query: string;
  domain_list_ids: string[];
  time_range?: TavilyTimeRange | null; // 検索期間: day, week, month, year
}

/**
 * ユーザー入力から主張と検索クエリ案を生成
 */
export const generateQueries = async (
  userInput: string,
): Promise<ApiResponse<QueryGenerationResponse>> => {
  try {
    const response = await apiRequest('/api/domain-factcheck/generate-queries', {
      method: 'POST',
      body: JSON.stringify({ user_input: userInput }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('クエリ生成エラー:', errorText);
      throw new Error(`クエリ生成に失敗しました: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('Query generation failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'クエリ生成に失敗しました',
    };
  }
};

/**
 * ドメイン制限付きでファクト検証を実行
 */
export const verifyWithDomains = async (
  request: VerifyWithDomainsRequest,
): Promise<ApiResponse<DomainVerificationResult>> => {
  try {
    const response = await apiRequest('/api/domain-factcheck/verify', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ドメイン検証エラー:', errorText);
      throw new Error(`検証に失敗しました: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('Domain verification failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '検証に失敗しました',
    };
  }
};

/**
 * 検証結果の判定色を取得
 */
export const getJudgementColor = (judgement: string): string => {
  switch (verdictKeyFromLabel(judgement)) {
    case 'accurate':
      return '#4ade80'; // green
    case 'inaccurate':
      return '#ef4444'; // red
    case 'partial':
      return '#fbbf24'; // yellow
    default:
      return '#94a3b8'; // gray
  }
};

/**
 * 検証結果の判定ラベルを取得
 */
export const getJudgementLabel = (judgement: string): string =>
  VERDICT_LABELS[verdictKeyFromLabel(judgement)];
